// ============================================================
// Pay Rocky - Sell Crypto Page
// ============================================================

import React, { useRef, useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { Copy, CheckCircle, AlertTriangle, Upload } from 'lucide-react';
import toast from 'react-hot-toast';

import { PublicLayout, PageContainer, SectionHeading } from '../components/layout/Layout';
import { Button, Card, FormField, TextInput, SelectInput, Divider } from '../components/ui/Primitives';
import { useAuth } from '../context/AuthContext';
import { useRates } from '../context/RatesContext';
import { useOrders } from '../context/OrdersContext';
import { useWallets } from '../context/WalletsContext';
import { ORDER_LIMITS, TRADABLE_CRYPTO_LIST, NIGERIAN_BANKS } from '../data/constants';
import { formatNGN, formatCrypto, formatCountdown, secondsUntil } from '../utils/helpers';

const STEPS = ['Configure', 'Bank Details', 'Deposit & Confirm'];

export default function SellPage() {
  const navigate = useNavigate();
  const { currentUser } = useAuth();
  const { rates } = useRates();
  const { placeSellOrder, uploadProof, cancelOrder, requestQuoteLock } = useOrders();
  const { getWalletAddress, getWalletConfig } = useWallets();

  const [step, setStep] = useState(0);
  const [loading, setLoading] = useState(false);
  const [placedOrder, setPlacedOrder] = useState(null);
  const [quoteLock, setQuoteLock] = useState(null);
  const [quoteCountdown, setQuoteCountdown] = useState(0);
  const [errors, setErrors] = useState({});

  const [form, setForm] = useState({
    cryptoId: 'usdt',
    network: 'TRC20',
    usdAmount: '500',
    bankName: 'Guaranty Trust Bank',
    accountName: '',
    accountNumber: '',
  });

  const crypto = TRADABLE_CRYPTO_LIST.find((item) => item.id === form.cryptoId);
  const sellLimits = ORDER_LIMITS.sell.byCrypto[form.cryptoId] || ORDER_LIMITS.sell.default;
  const rate = rates[form.cryptoId];
  const walletConfig = getWalletConfig(form.cryptoId, form.network);
  const depositWallet = getWalletAddress(form.cryptoId, form.network);
  const usdValue = parseFloat(form.usdAmount) || 0;
  const lockedUsdPrice = Number(quoteLock?.usd_price || rate?.usdPrice || 0);
  const lockedSellRate = Number(quoteLock?.rate_used || rate?.sell || 0);
  const cryptoAmt = lockedUsdPrice ? usdValue / lockedUsdPrice : 0;
  const nairaAmt = lockedUsdPrice ? (usdValue * lockedSellRate) / lockedUsdPrice : 0;

  useEffect(() => {
    const selected = TRADABLE_CRYPTO_LIST.find((item) => item.id === form.cryptoId);
    if (selected) setForm((prev) => ({ ...prev, network: selected.networks[0] }));
  }, [form.cryptoId]);

  useEffect(() => {
    let cancelled = false;
    if (!currentUser || !form.cryptoId || !form.network) {
      setQuoteLock(null);
      return undefined;
    }
    if (walletConfig?.isEnabled === false) {
      setQuoteLock(null);
      return undefined;
    }

    requestQuoteLock({
      type: 'sell',
      cryptoType: form.cryptoId,
      network: form.network,
    }).then((nextQuoteLock) => {
      if (!cancelled) setQuoteLock(nextQuoteLock);
    }).catch(() => {
      if (!cancelled) setQuoteLock(null);
    });

    return () => {
      cancelled = true;
    };
  }, [currentUser?.id, form.cryptoId, form.network, requestQuoteLock, walletConfig?.isEnabled]);

  useEffect(() => {
    if (!quoteLock?.expires_at) return undefined;
    setQuoteCountdown(secondsUntil(quoteLock.expires_at));
    const intervalId = window.setInterval(() => {
      const seconds = secondsUntil(quoteLock.expires_at);
      setQuoteCountdown(seconds);
      if (seconds === 0) window.clearInterval(intervalId);
    }, 1000);
    return () => window.clearInterval(intervalId);
  }, [quoteLock]);

  const set = (key) => (event) => setForm((prev) => ({ ...prev, [key]: event.target.value }));
  const setAccountNumber = (event) => {
    const digitsOnly = event.target.value.replace(/\D/g, '').slice(0, 10);
    setForm((prev) => ({ ...prev, accountNumber: digitsOnly }));
  };

  const validateStep0 = () => {
    const nextErrors = {};
    if (!usdValue || usdValue < sellLimits.minUsd) {
      nextErrors.usdAmount = `Minimum sell amount for ${crypto?.symbol || 'this asset'} is $${sellLimits.minUsd}`;
    }
    setErrors(nextErrors);
    return Object.keys(nextErrors).length === 0;
  };

  const validateStep1 = () => {
    const nextErrors = {};
    if (!form.accountName.trim()) nextErrors.accountName = 'Account name is required';
    if (!/^\d{10}$/.test(form.accountNumber)) nextErrors.accountNumber = 'Enter a valid 10-digit account number';
    setErrors(nextErrors);
    return Object.keys(nextErrors).length === 0;
  };

  const handleStep0 = () => {
    if (!currentUser) {
      navigate('/login');
      return;
    }
    if (walletConfig?.isEnabled === false) {
      toast.error(walletConfig?.disabledReason || `${form.network} is temporarily unavailable for sell orders.`);
      return;
    }
    if (validateStep0()) setStep(1);
  };

  const handleStep1 = () => {
    if (validateStep1()) setStep(2);
  };

  const handlePlaceOrder = async () => {
    setLoading(true);
    const response = await placeSellOrder({
      userId: currentUser.id,
      userName: currentUser.name,
      cryptoType: form.cryptoId,
      cryptoAmount: cryptoAmt,
      usdValue,
      nairaValue: nairaAmt,
      rateUsed: rate.sell,
      network: form.network,
      depositWallet,
      bankName: form.bankName,
      accountName: form.accountName,
      accountNumber: form.accountNumber,
      quoteLockToken: quoteLock?.token || '',
    });
    setLoading(false);

    if (response.success) {
      setPlacedOrder({ ...response.order, depositWallet: response.depositWallet });
      toast.success('Sell order created! Send crypto to the deposit address.');
    } else {
      toast.error(response.error || 'Failed to create order. Try again.');
    }
  };

  const copy = (text) => {
    navigator.clipboard.writeText(text);
    toast.success('Copied!');
  };

  return (
    <PublicLayout>
      <PageContainer style={{ maxWidth: '760px' }}>
        <SectionHeading tag="Sell Crypto" title="Sell crypto, receive Naira" subtitle="Send your crypto to our wallet and receive Naira directly to your bank account." />

        <StepBar steps={STEPS} current={step} />

        {step === 0 && (
          <Card className="fade-up">
            <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '14px' }}>
              <FormField label="Cryptocurrency">
                <SelectInput value={form.cryptoId} onChange={set('cryptoId')}>
                  {TRADABLE_CRYPTO_LIST.map((item) => <option key={item.id} value={item.id}>{item.name} ({item.symbol})</option>)}
                </SelectInput>
              </FormField>
              <FormField label="Network">
                <SelectInput value={form.network} onChange={set('network')}>
                  {crypto?.networks.map((network) => <option key={network} value={network}>{network}</option>)}
                </SelectInput>
              </FormField>
            </div>

            <FormField label="Amount to sell (USD)" error={errors.usdAmount}>
              <TextInput
                type="number"
                min={String(sellLimits.minUsd)}
                step="any"
                placeholder="e.g. 500"
                value={form.usdAmount}
                onChange={set('usdAmount')}
                error={errors.usdAmount}
              />
            </FormField>
            <div style={{ fontSize: '12px', color: 'var(--clr-text-3)', marginTop: '-8px', marginBottom: '1rem' }}>
              Minimum sell amount: ${sellLimits.minUsd}
            </div>

            {usdValue > 0 && rate && (
              <div style={{ background: 'var(--clr-surface-2)', borderRadius: 'var(--radius-md)', padding: '12px 16px', marginBottom: '1rem', border: '1px solid var(--clr-border)' }}>
                <Row l="You enter" v={`$${usdValue.toFixed(2)}`} />
                <Row l="You send" v={`${formatCrypto(cryptoAmt, crypto?.symbol)} ${crypto?.symbol}`} />
                <Row l="Sell rate" v={`${formatNGN(lockedSellRate)} / ${crypto?.symbol}`} />
                <div style={{ height: '1px', background: 'var(--clr-border)', margin: '8px 0' }} />
                <Row l="You receive" v={formatNGN(nairaAmt)} accent big />
              </div>
            )}

            {quoteLock ? (
              <div style={{ background: 'rgba(122, 75, 53, 0.08)', border: '1px solid rgba(122, 75, 53, 0.18)', borderRadius: 'var(--radius-md)', padding: '11px 14px', marginBottom: '1rem', fontSize: '12px', lineHeight: 1.7, color: 'var(--clr-text-2)' }}>
                Your sell quote is locked for this {crypto?.symbol} {form.network} route. Time remaining: <strong style={{ color: 'var(--clr-text)' }}>{formatCountdown(quoteCountdown)}</strong>.
              </div>
            ) : null}

            {walletConfig?.isEnabled === false ? (
              <div style={{ background: 'rgba(176, 58, 48, 0.08)', border: '1px solid rgba(176, 58, 48, 0.18)', borderRadius: 'var(--radius-md)', padding: '11px 14px', marginBottom: '1rem', fontSize: '12px', lineHeight: 1.7, color: 'var(--clr-text-2)' }}>
                {walletConfig?.disabledReason || `${form.network} is temporarily unavailable for this asset. Please choose another network or contact support.`}
              </div>
            ) : null}

            <Button variant="primary" size="lg" fullWidth onClick={handleStep0} disabled={walletConfig?.isEnabled === false}>
              Continue to Bank Details
            </Button>
          </Card>
        )}

        {step === 1 && (
          <Card className="fade-up">
            <div style={{ fontFamily: 'var(--font-display)', fontSize: '15px', fontWeight: 700, marginBottom: '16px' }}>
              Where should we send your Naira?
            </div>

            <FormField label="Bank">
              <SelectInput value={form.bankName} onChange={set('bankName')}>
                {NIGERIAN_BANKS.map((bank) => <option key={bank} value={bank}>{bank}</option>)}
              </SelectInput>
            </FormField>

            <FormField label="Account number" error={errors.accountNumber}>
              <TextInput type="tel" inputMode="numeric" pattern="[0-9]*" placeholder="10-digit account number" maxLength={10} value={form.accountNumber} onChange={setAccountNumber} error={errors.accountNumber} />
            </FormField>

            <FormField label="Account name" error={errors.accountName} hint="Must match your bank account exactly">
              <TextInput placeholder="As on your bank account" value={form.accountName} onChange={set('accountName')} error={errors.accountName} />
            </FormField>

            <Divider />
            <Row l="You will receive" v={formatNGN(nairaAmt)} accent big />
            <div style={{ height: '12px' }} />

            <div style={{ display: 'flex', gap: '10px' }}>
              <Button variant="ghost" onClick={() => setStep(0)}>Back</Button>
              <Button variant="primary" fullWidth onClick={handleStep1}>Continue</Button>
            </div>
          </Card>
        )}

        {step === 2 && (
          <Card className="fade-up">
            {!placedOrder ? (
              <>
                <div style={{ fontFamily: 'var(--font-display)', fontSize: '15px', fontWeight: 700, marginBottom: '16px' }}>
                  Confirm order summary
                </div>

                <ORow label="USD value" value={`$${usdValue.toFixed(2)}`} />
                <ORow label="You send" value={`${formatCrypto(cryptoAmt, crypto?.symbol)} ${crypto?.symbol} (${form.network})`} />
                <ORow label="You receive" value={formatNGN(nairaAmt)} accent big />
                <ORow label="Bank" value={form.bankName} />
                <ORow label="Account" value={`${form.accountNumber} - ${form.accountName}`} />
                <ORow label="Deposit wallet" value={depositWallet ? `${form.network} wallet configured` : `No ${form.network} wallet configured`} />

                {walletConfig?.isEnabled === false && (
                  <div style={{ background: 'rgba(176, 58, 48, 0.08)', border: '1px solid rgba(176, 58, 48, 0.16)', borderRadius: 'var(--radius-md)', padding: '10px 14px', fontSize: '12px', color: 'var(--clr-text-2)', marginTop: '1rem', lineHeight: 1.7 }}>
                    This network is currently disabled{walletConfig?.disabledReason ? `: ${walletConfig.disabledReason}` : '.'}
                  </div>
                )}

                <div style={{ background: 'var(--clr-warn-dim)', border: '1px solid rgba(255,184,48,0.25)', borderRadius: 'var(--radius-md)', padding: '10px 14px', fontSize: '12px', color: 'var(--clr-warn)', margin: '1rem 0', display: 'flex', gap: '8px' }}>
                  <AlertTriangle size={14} style={{ flexShrink: 0, marginTop: '1px' }} />
                  Only send the exact amount. Sending the wrong amount may delay your payout.
                </div>

                {!depositWallet && (
                  <div style={{ background: 'rgba(176, 58, 48, 0.08)', border: '1px solid rgba(176, 58, 48, 0.16)', borderRadius: 'var(--radius-md)', padding: '10px 14px', fontSize: '12px', color: 'var(--clr-text-2)', marginBottom: '1rem', lineHeight: 1.7 }}>
                    No PayRocky wallet has been configured yet for {crypto?.symbol} on {form.network}. PayRocky needs to add or replace that wallet before users can continue selling through this network.
                  </div>
                )}

                <div className="sell-confirm-actions" style={{ display: 'flex', gap: '10px' }}>
                  <Button variant="ghost" onClick={() => setStep(1)}>Back</Button>
                  <Button className="sell-confirm-submit" variant="primary" fullWidth loading={loading} onClick={handlePlaceOrder} disabled={!depositWallet || walletConfig?.isEnabled === false}>
                    Place Sell Order & Get Deposit Address
                  </Button>
                </div>
              </>
            ) : (
              <DepositView order={placedOrder} crypto={crypto} copy={copy} navigate={navigate} uploadProof={uploadProof} setPlacedOrder={setPlacedOrder} />
            )}
          </Card>
        )}
      </PageContainer>
      <style>{`
        .sell-confirm-submit {
          white-space: normal !important;
          text-align: center;
          line-height: 1.4;
        }

        .sell-proof-agreement__checkbox {
          justify-self: start;
        }

        .sell-proof-agreement__text {
          min-width: 0;
          overflow-wrap: anywhere;
        }

        @media (max-width: 640px) {
          .sell-confirm-actions {
            flex-direction: column !important;
          }

          .sell-proof-agreement {
            grid-template-columns: 18px minmax(0, 1fr) !important;
            gap: 8px !important;
          }

          .sell-proof-agreement__text {
            font-size: 11.5px !important;
            line-height: 1.65 !important;
          }
        }
      `}</style>
    </PublicLayout>
  );
}

function StepBar({ steps, current }) {
  return (
    <div style={{ display: 'flex', alignItems: 'center', marginBottom: '2rem', gap: '4px' }}>
      {steps.map((label, index) => (
        <React.Fragment key={label}>
          <div style={{ display: 'flex', alignItems: 'center', gap: '6px' }}>
            <div style={{
              width: '24px',
              height: '24px',
              borderRadius: '50%',
              background: index < current ? 'var(--clr-accent)' : index === current ? 'var(--clr-accent-dim)' : 'var(--clr-surface-2)',
              border: `1px solid ${index <= current ? 'var(--clr-accent)' : 'var(--clr-border)'}`,
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              fontSize: '10px',
              fontWeight: 700,
              color: index < current ? '#0a0a0b' : index === current ? 'var(--clr-accent)' : 'var(--clr-text-3)',
            }}>
              {index < current ? <CheckCircle size={12} /> : index + 1}
            </div>
            <span style={{ fontSize: '12px', fontWeight: index === current ? 600 : 400, color: index === current ? 'var(--clr-text)' : 'var(--clr-text-3)' }}>{label}</span>
          </div>
          {index < steps.length - 1 && <div style={{ flex: 1, height: '1px', background: index < current ? 'var(--clr-accent)' : 'var(--clr-border)' }} />}
        </React.Fragment>
      ))}
    </div>
  );
}

function Row({ l, v, accent, big }) {
  return (
    <div style={{ display: 'flex', justifyContent: 'space-between', marginBottom: '7px' }}>
      <span style={{ fontSize: '12px', color: 'var(--clr-text-3)' }}>{l}</span>
      <span style={{ fontSize: big ? '16px' : '13px', fontWeight: big ? 700 : 500, color: accent ? 'var(--clr-accent)' : 'var(--clr-text)' }}>{v}</span>
    </div>
  );
}

function ORow({ label, value, accent, big }) {
  return (
    <div style={{ display: 'flex', justifyContent: 'space-between', padding: '7px 0', borderBottom: '1px solid var(--clr-border)' }}>
      <span style={{ fontSize: '12px', color: 'var(--clr-text-3)' }}>{label}</span>
      <span style={{ fontSize: big ? '16px' : '13px', fontWeight: big ? 700 : 500, color: accent ? 'var(--clr-accent)' : 'var(--clr-text)' }}>{value}</span>
    </div>
  );
}

function DepositView({ order, crypto, copy, navigate, uploadProof, setPlacedOrder }) {
  const fileRef = useRef(null);
  const [agreed, setAgreed] = useState(false);
  const [loading, setLoading] = useState(false);
  const [fileName, setFileName] = useState('');
  const { cancelOrder } = useOrders();
  const awaitingProof = String(order?.status || '').toUpperCase() === 'PENDING';

  const handleUploadProof = async () => {
    const file = fileRef.current?.files?.[0];
    if (!agreed) {
      toast.error('Please review and accept the instruction note before uploading your screenshot.');
      return;
    }
    if (!file) {
      toast.error('Please select the screenshot of the transaction you sent.');
      return;
    }

    setLoading(true);
    const response = await uploadProof(order.order_id, file);
    setLoading(false);

    if (response.success) {
      setPlacedOrder((prev) => ({
        ...prev,
        ...response.order,
        depositWallet: prev?.depositWallet || prev?.wallet_address,
      }));
      toast.success('Transfer screenshot uploaded successfully.');
    } else {
      toast.error(response.error || 'We could not upload your screenshot right now.');
    }
  };

  const handleCancelOrder = async () => {
    setLoading(true);
    const response = await cancelOrder(order.order_id);
    setLoading(false);

    if (response.success) {
      toast.success(response.message || 'Order cancelled.');
      navigate('/dashboard');
    } else {
      toast.error(response.error || 'We could not cancel this order right now.');
    }
  };

  return (
    <div>
      <div style={{ display: 'flex', alignItems: 'center', gap: '10px', marginBottom: '20px' }}>
        <div style={{ width: '40px', height: '40px', borderRadius: '50%', background: 'var(--clr-accent-dim)', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
          <CheckCircle size={20} color="var(--clr-accent)" />
        </div>
        <div>
          <div style={{ fontFamily: 'var(--font-display)', fontSize: '15px', fontWeight: 700 }}>Order placed!</div>
          <div style={{ fontSize: '12px', color: 'var(--clr-text-2)' }}>
            Send <strong style={{ color: 'var(--clr-text)' }}>{formatCrypto(order.crypto_amount, crypto?.symbol)} {crypto?.symbol}</strong> to:
          </div>
        </div>
      </div>

      <div style={{ background: 'var(--clr-surface-2)', borderRadius: 'var(--radius-md)', padding: '1rem', marginBottom: '1rem', border: '1px solid var(--clr-border)' }}>
        <div style={{ fontSize: '11px', color: 'var(--clr-text-3)', marginBottom: '8px', textTransform: 'uppercase', letterSpacing: '0.06em', fontWeight: 600 }}>
          Deposit address ({order.network})
        </div>
        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', gap: '8px' }}>
          <div style={{ fontFamily: 'var(--font-mono)', fontSize: '12px', color: 'var(--clr-accent)', wordBreak: 'break-all', flex: 1 }}>
            {order.depositWallet || order.wallet_address}
          </div>
          <button onClick={() => copy(order.depositWallet || order.wallet_address)} style={{ background: 'var(--clr-surface-3)', border: '1px solid var(--clr-border)', borderRadius: 'var(--radius-sm)', padding: '6px 10px', cursor: 'pointer', color: 'var(--clr-text-2)', flexShrink: 0 }}>
            <Copy size={14} />
          </button>
        </div>
      </div>

      <div style={{ fontSize: '12px', color: 'var(--clr-text-2)', marginBottom: '1.5rem', lineHeight: 1.6 }}>
        After sending, our team will confirm the on-chain transaction and transfer <strong style={{ color: 'var(--clr-accent)' }}>{formatNGN(order.naira_value)}</strong> to your bank account within 30 minutes.
      </div>

      {awaitingProof ? (
        <div style={{ background: 'var(--clr-surface-2)', borderRadius: 'var(--radius-md)', border: '1px solid var(--clr-border)', padding: '1rem', marginBottom: '1rem' }}>
          <div style={{ fontSize: '13px', fontWeight: 700, color: 'var(--clr-text)', marginBottom: '8px' }}>
            Important review before you upload proof
          </div>
          <div style={{ fontSize: '12px', color: 'var(--clr-text-2)', lineHeight: 1.8, marginBottom: '12px' }}>
            Please review this carefully before proceeding: blockchain prices can move during confirmation, so the final amount you receive may differ slightly from the estimated amount shown above. We process your payout using the live operational rate at the point your transfer is fully confirmed.
          </div>
          <label className="sell-proof-agreement" style={{ display: 'grid', gridTemplateColumns: '18px minmax(0, 1fr)', alignItems: 'flex-start', gap: '10px', cursor: 'pointer' }}>
            <input
              className="sell-proof-agreement__checkbox"
              type="checkbox"
              checked={agreed}
              onChange={(event) => setAgreed(event.target.checked)}
              style={{ width: '16px', height: '16px', margin: '2px 0 0', accentColor: 'var(--clr-accent)', flexShrink: 0 }}
            />
            <span className="sell-proof-agreement__text" style={{ fontSize: '12px', color: 'var(--clr-text-2)', lineHeight: 1.7, minWidth: 0, overflowWrap: 'anywhere' }}>
              I have reviewed the instruction above and I understand that due to price fluctuation, there may be a slight difference between the estimated amount and the final amount I receive.
            </span>
          </label>
        </div>
      ) : null}

      {awaitingProof ? (
        <div style={{ background: 'linear-gradient(135deg, rgba(255,255,255,0.96) 0%, rgba(244, 235, 226, 0.94) 100%)', borderRadius: 'var(--radius-md)', border: '1px solid var(--clr-border)', padding: '1rem', marginBottom: '1.5rem' }}>
        <div style={{ fontSize: '13px', fontWeight: 700, color: 'var(--clr-text)', marginBottom: '8px' }}>
          Upload transfer screenshot
        </div>
        <div style={{ fontSize: '12px', color: 'var(--clr-text-2)', lineHeight: 1.7, marginBottom: '12px' }}>
          After sending your crypto to the wallet above, upload a screenshot of the successful transfer so our team can verify your payout faster.
        </div>

        <div
          onClick={() => fileRef.current?.click()}
          style={{
            border: '1px dashed var(--clr-border-md)',
            borderRadius: 'var(--radius-md)',
            padding: '14px 16px',
            background: 'rgba(255,255,255,0.72)',
            cursor: 'pointer',
            marginBottom: '12px',
          }}
        >
          <div style={{ display: 'flex', alignItems: 'center', gap: '10px', marginBottom: '6px' }}>
            <Upload size={16} color="var(--clr-accent)" />
            <span style={{ fontSize: '13px', fontWeight: 600, color: 'var(--clr-text)' }}>
              {fileName || order.proof_image ? (fileName || 'Screenshot selected and uploaded') : 'Choose transfer screenshot'}
            </span>
          </div>
          <div style={{ fontSize: '12px', color: 'var(--clr-text-3)' }}>
            Accepted: JPG, PNG, PDF
          </div>
        </div>

        <input
          ref={fileRef}
          type="file"
          accept=".jpg,.jpeg,.png,.pdf"
          style={{ display: 'none' }}
          onChange={(event) => setFileName(event.target.files?.[0]?.name || '')}
        />

        <Button variant="primary" fullWidth loading={loading} onClick={handleUploadProof} disabled={!agreed}>
          Upload Screenshot
        </Button>
        <div style={{ height: '10px' }} />
        <Button variant="ghost" fullWidth loading={loading} onClick={handleCancelOrder}>
          Cancel Order
        </Button>
        </div>
      ) : (
        <div style={{ background: 'rgba(42, 112, 64, 0.08)', border: '1px solid rgba(42, 112, 64, 0.16)', borderRadius: 'var(--radius-md)', padding: '1rem', marginBottom: '1.5rem' }}>
          <div style={{ fontSize: '13px', fontWeight: 700, color: 'var(--clr-text)', marginBottom: '8px' }}>
            Proof received
          </div>
          <div style={{ fontSize: '12px', color: 'var(--clr-text-2)', lineHeight: 1.7 }}>
            Your transfer proof has been submitted and this order is now under PayRocky review. You can track the latest status from your dashboard while the team completes settlement.
          </div>
        </div>
      )}

      <div style={{ fontSize: '11px', fontFamily: 'var(--font-mono)', color: 'var(--clr-text-3)', marginBottom: '1rem' }}>
        Order ID: <span style={{ color: 'var(--clr-text-2)' }}>{order.order_id}</span>
      </div>

      <Button variant="primary" fullWidth onClick={() => navigate('/dashboard')}>
        Track in Dashboard
      </Button>
    </div>
  );
}
